/**
 * What `jobs.services_required` holds, read tolerantly.
 *
 * The column is a jsonb array. Until 2026-09-02 every entry was a bare display
 * string — the service name as it read at write time, with no link to the
 * `service_types` row it came from. That is what let a tenant rename or
 * deactivate a service and orphan every job holding the old spelling (0367
 * repaired 156 of them; the cause was untouched).
 *
 * The API is moving the column to `{ id, name }` objects: `id` is the
 * `service_types` row for BEHAVIOUR (torque sheets, ticket AI), `name` is the
 * label as it stood when the job was written, for DISPLAY of historical
 * documents. The backfill and the API code ship together, so from that deploy
 * onward GET /jobs returns objects — and this bundle has to already read them,
 * or JobDetail renders an object as a React child and crashes.
 *
 * So: every reader goes through here, and here accepts BOTH shapes. A bare
 * string is a legacy entry; an object contributes its `name`. Anything else is
 * dropped. The screens keep working on names internally — the API resolves a
 * name it is sent back to the catalog row — so nothing on the write side
 * changes in this PR.
 */

export interface ServiceEntry {
  /** `service_types.id`, or null for a legacy bare-string entry. */
  id: string | null;
  /** The label as stored on the job. */
  name: string;
}

/** Coerce one raw jsonb element to an entry, or null if it is neither shape. */
export function toServiceEntry(raw: unknown): ServiceEntry | null {
  if (typeof raw === 'string') {
    const name = raw.trim();
    return name ? { id: null, name: raw } : null;
  }
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const name = typeof o.name === 'string' ? o.name : '';
    if (!name.trim()) return null;
    const id = typeof o.id === 'string' && o.id ? o.id : null;
    return { id, name };
  }
  return null;
}

/**
 * Coerce whatever the API handed back — an array, a JSON string of one, null —
 * into entries. Mirrors the API's own `parseServicesRequired` in accepting a
 * JSON string: the column has arrived stringified before.
 */
export function parseServiceEntries(raw: unknown): ServiceEntry[] {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  const out: ServiceEntry[] = [];
  for (const el of value) {
    const entry = toServiceEntry(el);
    if (entry) out.push(entry);
  }
  return out;
}

/** The display names, in stored order. What every screen renders and compares on. */
export function serviceNames(raw: unknown): string[] {
  return parseServiceEntries(raw).map((e) => e.name);
}

/** The catalog rows these helpers resolve against — a subset of ServiceType. */
export interface ServiceCatalogRowLike {
  id: string;
  name: string;
  isActive?: boolean;
}

/** trim → lowercase → collapse whitespace. Matching only; never stored. Mirrors the API's serviceKey(). */
export function serviceKey(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * What a DUPLICATED job starts with: the source job's services, resolved
 * against the catalog AS IT IS NOW.
 *
 * A duplicate is a new job. Carrying the source's stored entries forward
 * verbatim was the one live path still minting old spellings after the
 * catalog arrived (PAR J-0054, 2026-08-28), and after 0368 it would carry a
 * stale snapshot name onto a job written today. So each entry is re-resolved:
 *
 *   - an entry with an id the catalog knows → that row's CURRENT name, active
 *     or not (a retired row renders "No longer offered" and the user can
 *     untick it — dropping it silently is the bug this screen exists to avoid)
 *   - a bare string, or an id the catalog does not know → matched by name;
 *     on a match, the row's current name; otherwise the string as-is, so the
 *     API can resolve a retired spelling through its alias map or refuse it
 *     out loud
 *
 * De-duplicated by resolved name, first occurrence wins, order preserved.
 */
export function resolveServicesForCopy(raw: unknown, catalog: ServiceCatalogRowLike[]): string[] {
  const byId = new Map(catalog.map((r) => [r.id, r]));
  const byKey = new Map(catalog.map((r) => [serviceKey(r.name), r]));
  const out: string[] = [];
  const seen = new Set<string>();
  for (const entry of parseServiceEntries(raw)) {
    const row = (entry.id && byId.get(entry.id)) || byKey.get(serviceKey(entry.name));
    const name = row ? row.name : entry.name;
    const key = serviceKey(name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

/** One element of a job WRITE: a resolved row, or a bare name the API resolves itself. */
export type ServiceWriteEntry = { id: string; name: string } | string;

/**
 * What the form SENDS. The checkbox set works on names; the API stores
 * { id, name } (0368) and accepts a bare string only as an old-client
 * tolerance that it logs on every request. So a name the catalog knows goes
 * up as the row — id plus current name — and only a name the catalog does
 * not hold (a legacy label on an old job, kept ticked) goes up bare, for the
 * API's alias map to resolve or refuse.
 */
export function serviceEntriesForWrite(names: string[], catalog: ServiceCatalogRowLike[]): ServiceWriteEntry[] {
  const byKey = new Map(catalog.map((r) => [serviceKey(r.name), r]));
  return names.map((name) => {
    const row = byKey.get(serviceKey(name));
    return row ? { id: row.id, name: row.name } : name;
  });
}
